import jsPDF from 'jspdf';
import { Client, Order, UserAccount } from '../types';

const formatFcfaAmount = (value: number): string => {
  const safe = Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
  const grouped = safe
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    .replace(/[\u00A0\u202F]/g, ' ');
  return `${grouped} F CFA`;
};

const normaliseLabel = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const clampLines = (lines: string[], maxLines: number): string[] => {
  if (lines.length <= maxLines) return lines;
  const trimmed = lines.slice(0, maxLines);
  trimmed[maxLines - 1] = `${trimmed[maxLines - 1].replace(/\s+$/, '')}...`;
  return trimmed;
};

const loadImage = (url: string, timeoutMs: number = 5000): Promise<string> => {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`Timeout: Chargement de l'image a depasse ${timeoutMs}ms`));
    }, timeoutMs);

    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      clearTimeout(timeout);
      try {
        const targetWidth = img.naturalWidth || img.width || 512;
        const targetHeight = img.naturalHeight || img.height || 512;
        const scale = 2;

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth * scale;
        canvas.height = targetHeight * scale;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/png'));
        } else {
          reject(new Error('Format de canvas non supporte'));
        }
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => {
      clearTimeout(timeout);
      reject(new Error(`Erreur reseau lors du chargement: ${url}`));
    };
    img.src = url;
  });
};

export const generateReceiptPDF = async (
  order: Order,
  client: Client,
  user: UserAccount
): Promise<void> => {
  console.log('--- Demarrage de la generation du PDF ---');

  try {
    if (!order || !client) {
      throw new Error('Donnees de commande ou client manquantes');
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a6',
    });

    const width = doc.internal.pageSize.getWidth();
    const height = doc.internal.pageSize.getHeight();
    const margin = 8;
    let y = 12;

    const ACCENT: [number, number, number] = [79, 70, 229];
    const DARK: [number, number, number] = [30, 41, 59];
    const LIGHT_GRAY: [number, number, number] = [248, 250, 252];
    const BORDER: [number, number, number] = [226, 232, 240];

    doc.setFillColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    doc.rect(0, 0, width, 2, 'F');

    let logoBottomY = y;
    const defaultLogoUrl = `${window.location.origin}/logo.svg`;
    const logoToUse = user.workshop?.logo_url || defaultLogoUrl;

    try {
      const imgData = await loadImage(logoToUse, 3000);
      const logoSize = 18;
      doc.addImage(imgData, 'PNG', (width - logoSize) / 2, y, logoSize, logoSize);
      logoBottomY = y + logoSize;
      y += logoSize + 4;
    } catch {
      try {
        const defaultImgData = await loadImage(defaultLogoUrl, 2000);
        const logoSize = 18;
        doc.addImage(defaultImgData, 'PNG', (width - logoSize) / 2, y, logoSize, logoSize);
        logoBottomY = y + logoSize;
        y += logoSize + 4;
      } catch {
        y += 2;
      }
    }

    y = Math.max(y, logoBottomY + 7);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(DARK[0], DARK[1], DARK[2]);
    const wsName = normaliseLabel((user.workshop?.name || user.full_name || 'ATELIER DE COUTURE').toUpperCase());
    let wsNameFontSize = 16;
    doc.setFontSize(wsNameFontSize);
    while (wsNameFontSize > 11 && doc.getTextWidth(wsName) > width - margin * 2) {
      wsNameFontSize -= 1;
      doc.setFontSize(wsNameFontSize);
    }
    doc.text(wsName, width / 2, y, { align: 'center' });
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    const subtitle = normaliseLabel(user.workshop?.slogan || user.workshop?.address || '');
    if (subtitle) {
      const subtitleLines = clampLines(doc.splitTextToSize(subtitle, width - margin * 2), 2);
      doc.text(subtitleLines, width / 2, y, { align: 'center' });
      y += subtitleLines.length * 3.5;
    }

    if (user.workshop?.phone) {
      doc.text(`Tel: ${normaliseLabel(user.workshop.phone)}`, width / 2, y, { align: 'center' });
      y += 6;
    } else {
      y += 2;
    }

    doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
    doc.setLineWidth(0.1);
    doc.line(margin, y, width - margin, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    const title = order.paid ? 'RECU DE PAIEMENT' : 'BON DE COMMANDE';
    doc.text(title, margin, y);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    const orderRef = (order.id || 'ORD-TEMP').toString().split('-')[0].toUpperCase();
    doc.text(`#${orderRef}`, width - margin, y, { align: 'right' });
    y += 6;

    doc.setFillColor(LIGHT_GRAY[0], LIGHT_GRAY[1], LIGHT_GRAY[2]);
    doc.roundedRect(margin, y, width - margin * 2, 18, 2, 2, 'F');

    doc.setFontSize(6);
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'bold');
    doc.text('FACTURE A', margin + 4, y + 6);
    doc.text("DATE D'EMISSION", width - margin - 4, y + 6, { align: 'right' });

    doc.setFontSize(10);
    doc.setTextColor(DARK[0], DARK[1], DARK[2]);
    doc.text(normaliseLabel(client.name || 'Client'), margin + 4, y + 12);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(new Date().toLocaleDateString('fr-FR'), width - margin - 4, y + 12, { align: 'right' });
    y += 24;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('DESCRIPTION', margin, y);
    doc.text('TOTAL', width - margin, y, { align: 'right' });

    y += 2;
    doc.setDrawColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    doc.setLineWidth(0.3);
    doc.line(margin, y, width - margin, y);
    y += 6;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(DARK[0], DARK[1], DARK[2]);

    const amountMaxWidth = 42;
    const orderTitleRaw = order.title || (order.outfitType ? `Confection ${order.outfitType}` : 'Travaux de couture');
    const orderTitle = normaliseLabel(orderTitleRaw);
    const orderTitleMaxWidth = width - margin * 2 - amountMaxWidth - 2;
    const orderTitleLines = clampLines(doc.splitTextToSize(orderTitle, orderTitleMaxWidth), 2);
    doc.text(orderTitleLines, margin, y);

    const amountVal = order.negotiatedAmount ?? 0;
    const amountStr = amountVal > 0 ? formatFcfaAmount(amountVal) : '---';
    let amountFontSize = 9;
    while (amountFontSize > 7 && doc.getTextWidth(amountStr) > amountMaxWidth) {
      amountFontSize -= 1;
      doc.setFontSize(amountFontSize);
    }
    doc.text(amountStr, width - margin, y, { align: 'right' });

    doc.setFontSize(9);
    y += orderTitleLines.length * 4 + 1;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    const notesText = normaliseLabel(order.notes || 'Articles confectionnes sur mesure.');
    const splitNotes = clampLines(doc.splitTextToSize(notesText, width - margin * 2 - 4), 3);
    doc.text(splitNotes, margin, y);
    y += splitNotes.length * 4 + 2;

    if (!order.paid && order.dueDate) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(DARK[0], DARK[1], DARK[2]);
      try {
        const dDate = new Date(order.dueDate).toLocaleDateString('fr-FR');
        doc.text(`Livraison prevue: ${dDate}`, margin, y);
        y += 6;
      } catch {
        // ignore invalid date
      }
    }

    y = Math.min(Math.max(y, height - 45), height - 35);

    doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
    doc.setLineWidth(0.1);
    doc.line(margin, y, width - margin, y);
    y += 6;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    if (order.paid) {
      doc.setFillColor(209, 250, 229);
      doc.setTextColor(6, 95, 70);
      doc.roundedRect(width - 45, y - 4, 37, 6, 1, 1, 'F');
      doc.text('PAYE EN TOTALITE', width - 26.5, y, { align: 'center' });
    } else {
      doc.setFillColor(254, 243, 199);
      doc.setTextColor(146, 64, 14);
      doc.roundedRect(width - 45, y - 4, 37, 6, 1, 1, 'F');
      doc.text('SOLDE EN ATTENTE', width - 26.5, y, { align: 'center' });
    }

    y += 10;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(DARK[0], DARK[1], DARK[2]);
    doc.text('Elegance & Precision', margin, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(148, 163, 184);
    doc.text('Document genere par Mesurette', width / 2, height - 6, { align: 'center' });

    doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
    doc.line(margin, height - 20, margin + 25, height - 20);
    doc.setFontSize(5);
    doc.text('SIGNATURE / CACHET', margin, height - 17);

    try {
      const sealData = await loadImage(defaultLogoUrl, 1000);
      doc.addImage(sealData, 'PNG', margin + 5, height - 15, 8, 8);
    } catch {
      // ignored
    }

    const filename = `${title.replace(/\s+/g, '_')}_${(client.name || 'Client').replace(/\s+/g, '_')}.pdf`;
    doc.save(filename);
    console.log('PDF sauvegarde avec succes:', filename);
  } catch (error) {
    console.error('ERREUR CRITIQUE GENERATION PDF:', error);
    throw error;
  }
};
